'use client'

/**
 * Hyperliquid auto-deposit watcher.
 *
 * Architecture:
 *   1. Poll user's Arbitrum USDC balance every 12s (gas-friendly).
 *   2. Establish baseline on first successful read — pre-existing USDC is
 *      NOT swept (would be surprise behavior for users who hold USDC on
 *      Arbitrum for other reasons).
 *   3. On subsequent reads, if balance grows by ≥ HYPERLIQUID_MIN_DEPOSIT_USDC
 *      compared to the last known floor, fire approve + Bridge2.deposit
 *      via useHyperliquidDeposit. Update the floor to the post-deposit
 *      balance so the next delta is independently detected.
 *   4. Surface toasts: "Detected $X — depositing to Hyperliquid…", then
 *      "$X credited" once HyperCore clearinghouse balance bumps.
 *
 * Mount once globally (portfolio layout or app root). Polling only runs
 * when the user is authenticated (eoaAddress is set).
 */

import {
	HYPERLIQUID_ARB_USDC,
	HYPERLIQUID_IS_TESTNET,
	HYPERLIQUID_MIN_DEPOSIT_USDC,
} from '@/constants/hyperliquid'
import { useHyperliquidAgent } from '@/hooks/useHyperliquidAgent'
import { useHyperliquidBalance } from '@/hooks/useHyperliquidBalance'
import { useHyperliquidDeposit } from '@/hooks/useHyperliquidDeposit'
import { useHyperliquidPerpToSpot } from '@/hooks/useHyperliquidPerpToSpot'
import { useHyperliquidUsdcToUsdh } from '@/hooks/useHyperliquidUsdcToUsdh'
import { hlLog } from '@/lib/hyperliquid/debug'
import {
	isDepositLocked,
	releaseDepositLock,
	tryClaimDepositLock,
} from '@/lib/hyperliquid/deposit-lock'
import { useWallet } from '@/providers/WalletContext'
import { useQuery } from '@tanstack/react-query'
import { useTranslations } from 'next-intl'
import { usePathname } from 'next/navigation'
import { useEffect, useRef } from 'react'
import { toast } from 'sonner'
import { http, type Address, createPublicClient, erc20Abi, formatUnits } from 'viem'
import { arbitrum, arbitrumSepolia } from 'viem/chains'

const ARB_USDC = HYPERLIQUID_ARB_USDC as Address

// Mainnet uses dedicated Alchemy node for higher rate limits; testnet falls
// back to the public Arbitrum Sepolia RPC since Alchemy's testnet Alchemy
// endpoint is also available but rarely worth a separate API key for
// non-prod traffic.
const arbitrumClient = createPublicClient({
	chain: HYPERLIQUID_IS_TESTNET ? arbitrumSepolia : arbitrum,
	transport: http(
		HYPERLIQUID_IS_TESTNET
			? 'https://sepolia-rollup.arbitrum.io/rpc'
			: process.env.NEXT_PUBLIC_ALCHEMY_API_KEY
				? `https://arb-mainnet.g.alchemy.com/v2/${process.env.NEXT_PUBLIC_ALCHEMY_API_KEY}`
				: 'https://arb1.arbitrum.io/rpc'
	),
})

async function readArbitrumUsdc(addr: Address): Promise<number> {
	const raw = await arbitrumClient.readContract({
		address: ARB_USDC,
		abi: erc20Abi,
		functionName: 'balanceOf',
		args: [addr],
	})
	return Number.parseFloat(formatUnits(raw, 6))
}

export function useHyperliquidAutoDeposit() {
	const t = useTranslations('hyperliquid.toast')
	const { eoaAddress } = useWallet()
	const pathname = usePathname()
	const deposit = useHyperliquidDeposit()
	const balance = useHyperliquidBalance(Boolean(eoaAddress))
	const moveToSpot = useHyperliquidPerpToSpot()
	const convertUsdhToUsdc = useHyperliquidUsdcToUsdh()
	const { status: agentStatus } = useHyperliquidAgent()
	const agentApproved = agentStatus.data?.alreadyApproved === true

	// Bridge2 deposit is silent (sponsored ERC20 transfer, no wallet popup),
	// so the watcher can run globally. The auto-FUND sequence — perp→spot
	// (user EIP-712 popup) — must NOT pop a wallet prompt when the user is
	// browsing unrelated markets. Restrict the funding step to pages where
	// the user is actually engaging with Hyperliquid.
	const onHyperliquidPage = Boolean(
		pathname && (pathname.includes('/v2/hyperliquid/') || pathname.startsWith('/portfolio'))
	)
	// Drives the auto-funding sequence:
	//  - `fundingInFlightRef` blocks concurrent runs (the same useEffect can
	//    re-fire while the async chain is still running because balance.data
	//    reference changes after refetch).
	//  - `fundedSnapshotRef` records the exact balance triple that triggered
	//    the most-recent run so we don't immediately re-fire when balance
	//    polls back with the same numbers post-success (or post-error). New
	//    deposits change the snapshot and unblock the next run.
	const fundingInFlightRef = useRef(false)
	const fundedSnapshotRef = useRef<string | null>(null)

	// Synchronous lock for the deposit effect. `deposit.isPending` is
	// observable React state — between `mutate()` returning and React
	// committing the new render, a second synchronous effect run still
	// sees `isPending=false` and would fire a duplicate `Bridge2.deposit`.
	// The ref is the atomic guard; it flips before mutate() runs and resets
	// in `onSettled`.
	const depositInFlightRef = useRef(false)

	// Post-fire EOA balance the watcher EXPECTS once Bridge2.deposit lands
	// (= current - amount, set synchronously when firing). Re-fire is gated
	// on the live balance growing meaningfully ABOVE this floor — i.e. a
	// genuinely new deposit, not the same balance the watcher is already
	// working on.
	const floorRef = useRef<number | null>(null)
	// Tracks the HL balance baseline so the "credited" toast fires exactly
	// once when funds actually land on HyperCore (≥3min later).
	const pendingDepositRef = useRef<{ amount: number; preHlBalance: number } | null>(null)
	// Cool-off after a sequence of failures so a misconfigured paymaster or
	// stuck userOp doesn't poll Alchemy every 12s forever. Resets when the
	// user manually triggers (Phase 3) or balance changes meaningfully.
	const failureCountRef = useRef(0)
	const lastFailedBalanceRef = useRef<number | null>(null)

	const arbUsdc = useQuery({
		queryKey: ['arb-usdc-balance', eoaAddress],
		queryFn: () => readArbitrumUsdc(eoaAddress as Address),
		enabled: Boolean(eoaAddress),
		refetchInterval: 12_000,
	})

	useEffect(() => {
		if (!eoaAddress || arbUsdc.data === undefined) return
		// Module-level + ref locks. Module-level (shared with the manual
		// deposit modal + stranded-deposit banner) survives StrictMode's
		// effect double-invoke and prevents concurrent userOps across
		// independent React components. useRef is the same-instance fast
		// path. Both must be free; the claim happens synchronously below.
		if (isDepositLocked()) return
		if (depositInFlightRef.current) return
		// Redundant safety: post-mutate render flips this.
		if (deposit.isPending) return

		const current = arbUsdc.data

		// Trigger on absolute balance ≥ Bridge2 minimum.
		if (current < HYPERLIQUID_MIN_DEPOSIT_USDC) return

		// Withdraw cool-off — when the user has just withdrawn from HL,
		// validators credit the destination EOA ~3-4min later. Without this
		// guard the watcher would re-deposit the funds immediately.
		try {
			const expiry = Number(localStorage.getItem('hl-withdraw-in-progress') ?? '0')
			if (expiry > Date.now()) return
			if (expiry > 0) localStorage.removeItem('hl-withdraw-in-progress')
		} catch {}

		// Cool-off after 3 failures at the same balance — paymaster or
		// userOp is wedged; don't pound Alchemy every 12s.
		if (
			failureCountRef.current >= 3 &&
			lastFailedBalanceRef.current !== null &&
			Math.abs(current - lastFailedBalanceRef.current) < 0.1
		) {
			return
		}

		// Skip when there's no NEW deposit above the floor. floorRef is set
		// at fire-time to the EXPECTED post-deposit balance (current - amount),
		// so a fresh deposit must lift the live balance ≥ MIN_DEPOSIT above
		// that floor. Without this, a still-pending userOp leaves the live
		// balance untouched and a re-render would re-fire on the same balance.
		if (floorRef.current !== null && current < floorRef.current + HYPERLIQUID_MIN_DEPOSIT_USDC) {
			return
		}

		// Floor to 2 decimals (not whole dollars) — 1c cushion against
		// floating-point off-by-one in parseUnits.
		const amount = Math.floor((current - 0.01) * 100) / 100
		if (amount < HYPERLIQUID_MIN_DEPOSIT_USDC) return

		// Claim BOTH locks + floor BEFORE invoking mutate(). All three set
		// synchronously so the next effect tick — whether from StrictMode
		// double-invoke (which resets useRef but not module state), HMR,
		// or a fast dep change — sees the lock held.
		if (!tryClaimDepositLock()) return
		depositInFlightRef.current = true
		floorRef.current = current - amount

		// One last freshness check before firing. `arbUsdc.data` is from a
		// 12s-poll TanStack query — between two polls a deposit from
		// another tab / the manual modal / the recovery banner could have
		// already drained the EOA, leaving `current` referring to a balance
		// that no longer exists on-chain. Firing on that stale view triggers
		// a wasted 5-retry paymaster loop that all 400 with "exceeds
		// balance" and ends in silent-success detection — works, but it's
		// 30 seconds of red errors in the console for nothing. Fresh read
		// here aborts cleanly before any userOp is built. Release the lock
		// + reset the floor so a future genuinely-fresh balance can trigger.
		;(async () => {
			const fresh = await arbUsdc.refetch()
			const freshCurrent = fresh.data
			if (freshCurrent === undefined || Math.floor((freshCurrent - 0.01) * 100) / 100 < amount) {
				hlLog('[hl-watcher] ABORT — fresh balance below committed amount', {
					cached: current,
					fresh: freshCurrent,
					committedAmount: amount,
				})
				releaseDepositLock()
				depositInFlightRef.current = false
				floorRef.current = null
				return
			}
			fireDeposit()
		})().catch(err => {
			// Pre-flight refetch failed (RPC error etc.). Release locks so the
			// next poll cycle can try again on fresh data.
			console.error('[hl-watcher] pre-flight refetch failed', err)
			releaseDepositLock()
			depositInFlightRef.current = false
			floorRef.current = null
		})
		return

		// Synchronous fire path — moved into a nested function so the
		// pre-flight async check above can short-circuit without code
		// duplication. Hoisting via `function` so the reference earlier
		// in this effect (the `void (async)` IIFE) sees it.
		function fireDeposit() {
			hlLog('[hl-watcher] FIRE deposit', {
				arbBalance: current,
				amount,
				newFloor: floorRef.current,
			})

			toast.loading(t('depositLoading', { amount: String(amount) }), {
				id: 'hl-auto-deposit',
				description: t('depositLoadingDesc'),
			})

			// Snapshot TOTAL pre-deposit HL balance (perp+spotUsdc+spotUsdh), not
			// just perp.withdrawable. The credited-toast effect compares against
			// this same total to handle the case where auto-fund moves the freshly
			// credited perp → spot before the toast effect samples.
			const preTotal = balance.data
				? Number.parseFloat(balance.data.perp?.withdrawable ?? balance.data.withdrawable) +
					(balance.data.spot ? Number.parseFloat(balance.data.spot.usdc) : 0) +
					(balance.data.spot ? Number.parseFloat(balance.data.spot.usdh) : 0)
				: 0
			pendingDepositRef.current = { amount, preHlBalance: preTotal }

			deposit.mutate(
				{ amountUsdc: amount },
				{
					onError: async err => {
						const message = (err as Error).message
						console.error('[hl-auto-deposit] failed', err)
						// Detect "silent success": the 5-retry loop inside
						// useHyperliquidDeposit matches /exceeds balance/ as a
						// paymaster race and retries. If the FIRST attempt
						// actually succeeded on-chain but the bundler response
						// was lost (network blip, ws timeout), the next retries
						// see a drained EOA and all error with "exceeds
						// balance" — the mutation throws, but the userOp DID
						// land. Refetch arb balance here; if it's gone, treat
						// the failure as a silent success and leave
						// pendingDepositRef in place so the credit-detection
						// effect fires when HL credits.
						let drained = false
						try {
							const fresh = await arbUsdc.refetch()
							if (fresh.data !== undefined && fresh.data < amount * 0.5) {
								drained = true
							}
						} catch {}
						if (drained) {
							console.warn(
								'[hl-auto-deposit] response lost but EOA drained — deposit succeeded silently'
							)
							toast.dismiss('hl-auto-deposit')
							return
						}
						pendingDepositRef.current = null
						failureCountRef.current += 1
						lastFailedBalanceRef.current = current
						// Reset floor so the next tick can fire again if the balance
						// hasn't actually been drained. Within the cool-off (same
						// balance, 3+ fails) the failure-count check still bails.
						floorRef.current = null
						if (failureCountRef.current >= 3) {
							// Cross-component signal for HyperliquidStrandedDepositBanner:
							// only show the manual-retry banner once the watcher has
							// genuinely given up, not during normal post-bridge windows.
							try {
								localStorage.setItem('hl-auto-deposit-paused-at', String(Date.now()))
							} catch {}
							toast.error(t('depositFailed'), {
								id: 'hl-auto-deposit',
								description: message.slice(0, 200),
							})
						}
						// Intermediate failures (count 1-2): no toast. The previous
						// "Retrying Hyperliquid deposit…" toast was misleading
						// because the actual retry happens silently on the next
						// 12s arbBalance refetch — and if the balance was already
						// drained (success path above), there's no retry at all.
					},
					onSuccess: () => {
						failureCountRef.current = 0
						lastFailedBalanceRef.current = null
						try {
							localStorage.removeItem('hl-auto-deposit-paused-at')
						} catch {}
					},
					onSettled: () => {
						depositInFlightRef.current = false
						releaseDepositLock()
					},
				}
			)
		}
	}, [eoaAddress, arbUsdc.data, deposit, balance.data, t])

	// Watch HL clearinghouse for the validator-credited bump and close the toast.
	//
	// Total comparison (perp + spotUsdc + spotUsdh) handles the case where the
	// auto-fund sequence sweeps perp → USDH before this effect samples
	// balance.withdrawable — without it, perp drops to ~0 immediately after
	// step 1 and the "credited" toast would never fire even though the funds
	// did arrive on HL.
	useEffect(() => {
		const pending = pendingDepositRef.current
		if (!pending) return
		if (!balance.data) return
		const hlPerp = Number.parseFloat(balance.data.perp?.withdrawable ?? balance.data.withdrawable)
		const hlSpotUsdc = balance.data.spot ? Number.parseFloat(balance.data.spot.usdc) : 0
		const hlSpotUsdh = balance.data.spot ? Number.parseFloat(balance.data.spot.usdh) : 0
		const hlNow = hlPerp + hlSpotUsdc + hlSpotUsdh
		if (hlNow >= pending.preHlBalance + pending.amount * 0.9) {
			// >=90% threshold tolerates any small fee deduction (HIP-4 fees are
			// currently 0 but defensive); validators credit the full amount.
			toast.success(t('depositCredited', { amount: String(pending.amount) }), {
				id: 'hl-auto-deposit',
			})
			pendingDepositRef.current = null
		}
	}, [balance.data, t])

	// Auto-funding: perp USDC → spot USDC (+ legacy USDH→USDC migration).
	//
	// Bridge2 deposits land in PERP. Outcome markets need USDC on spot.
	// We move perp→spot automatically. If legacy USDH is present (≥$10),
	// we also convert it to USDC via @230.
	//
	// One USER signature is fundamentally required (usdClassTransfer is a
	// user-class action — agents are not authorized to sign it). The
	// USDC→USDH conversion is agent-signed and silent.
	useEffect(() => {
		if (!balance.data) return
		if (fundingInFlightRef.current) return
		// Don't auto-fund while a Bridge2 deposit is still pending — we'd
		// shovel partially-credited balance and the user would see two
		// signature prompts back to back.
		if (pendingDepositRef.current !== null) return
		// Consent gate — never pop a wallet signature prompt unless the
		// user is on a Hyperliquid surface. Anywhere else (Polymarket
		// market, Explore, etc.) the prompt would be unexpected.
		if (!onHyperliquidPage) return
		// Don't auto-fund mid-WITHDRAW — the withdraw flow sells spot USDH
		// to spot USDC and then asks the user to sign a usdClassTransfer
		// for the FULL USDC balance. If the auto-fund effect fires between
		// the sell and the user signing the transfer, it grabs the USDC
		// for itself (converts back to USDH) and the subsequent transfer
		// fails with "Insufficient balance for token transfer". The
		// withdraw flow sets `hl-withdraw-in-progress` at its start (not
		// at withdraw3) so this gate covers the whole pipeline.
		try {
			const expiry = Number(localStorage.getItem('hl-withdraw-in-progress') ?? '0')
			if (expiry > Date.now()) return
		} catch {}

		const perp = Number.parseFloat(balance.data.perp?.withdrawable ?? balance.data.withdrawable)
		const spotUsdc = balance.data.spot ? Number.parseFloat(balance.data.spot.usdc) : 0
		const spotUsdh = balance.data.spot ? Number.parseFloat(balance.data.spot.usdh) : 0

		// Trigger when there's USDC to move OR stranded USDH to migrate.
		const totalUsdcAvailable = perp + spotUsdc
		const needsUsdhMigration = spotUsdh >= 10 && agentApproved
		if (totalUsdcAvailable < 10 && !needsUsdhMigration) return
		// Skip if there's nothing to do: no perp to move AND no USDH to migrate
		if (perp < 1 && spotUsdc >= 10 && !needsUsdhMigration) return

		const snapshot = `${perp.toFixed(2)}|${spotUsdc.toFixed(2)}|${spotUsdh.toFixed(2)}`
		if (fundedSnapshotRef.current === snapshot) return

		fundingInFlightRef.current = true
		fundedSnapshotRef.current = snapshot
		// By the time auto-fund kicks in, the deposit-side toast lifecycle
		// is done — but Sonner occasionally leaves a stale `loading`
		// (e.g. the "Retrying Hyperliquid deposit…" toast from a transient
		// paymaster race) hanging around because the success toast that
		// SHOULD replace it didn't fire (credit-detection effect bailed
		// out due to a stale snapshot). Explicit dismiss here guarantees
		// the deposit-side toast surface is clean before we start showing
		// the auto-fund "completing setup (1/2)…" sequence.
		toast.dismiss('hl-auto-deposit')
		hlLog('[hl-autofund] start', {
			perp,
			spotUsdc,
			snapshot,
			onPage: pathname,
		})
		;(async () => {
			try {
				if (perp > 0) {
					// Re-check withdraw flag right before firing — the
					// withdrawal flow may have set it between the synchronous
					// guard and this async point.
					try {
						const exp = Number(localStorage.getItem('hl-withdraw-in-progress') ?? '0')
						if (exp > Date.now()) {
							console.warn('[hl-autofund] SKIP — withdraw started since guard check')
							fundedSnapshotRef.current = null
							return
						}
					} catch {}
					toast.loading(t('setupStep1'), {
						id: 'hl-auto-fund',
						description: t('setupStep1Desc'),
					})
					await moveToSpot.mutateAsync({ amount: perp.toFixed(2), toPerp: false })
				}
				// Step 2: migrate leftover USDH → USDC if present.
				// HL switched outcome collateral from USDH to USDC — users
				// who deposited under the old flow have stranded USDH.
				if (agentApproved) {
					let freshSpotUsdh = 0
					try {
						const refetched = await balance.refetch()
						if (refetched.data?.spot) {
							freshSpotUsdh = Number.parseFloat(refetched.data.spot.usdh)
						}
					} catch {}
					if (freshSpotUsdh >= 10) {
						try {
							toast.loading(t('setupStep2'), {
								id: 'hl-auto-fund',
								description: t('setupStep2Desc'),
							})
							await convertUsdhToUsdc.mutateAsync({
								usdhAmount: freshSpotUsdh.toFixed(2),
								reverse: true,
							})
						} catch (migrationErr) {
							console.warn('[hl-autofund] USDH→USDC migration failed (non-fatal)', migrationErr)
							// Null snapshot so next effect tick retries
							fundedSnapshotRef.current = null
						}
					}
				}
				let finalUsdc = 0
				try {
					const final = await balance.refetch()
					if (final.data?.spot) {
						finalUsdc = Number.parseFloat(final.data.spot.usdc)
					}
				} catch {}
				if (finalUsdc >= 10) {
					hlLog('[hl-autofund] complete — ready to trade')
					toast.success(t('readyToTrade'), {
						id: 'hl-auto-fund',
						description: t('readyToTradeDesc'),
					})
				} else {
					toast.dismiss('hl-auto-fund')
				}
			} catch (err) {
				fundedSnapshotRef.current = null
				const message = err instanceof Error ? err.message : 'unknown error'
				console.error('[hl-auto-fund] failed', err)
				toast.error(t('setupPaused'), {
					id: 'hl-auto-fund',
					description: t('setupPausedDesc', { message: message.slice(0, 180) }),
				})
			} finally {
				fundingInFlightRef.current = false
			}
		})()
		// Intentionally NOT depending on `moveToSpot`,
		// `balance.refetch` — those are useMutation/useQuery return values
		// that get new object refs every render, which would fire this
		// effect on every render. The closures over them still call the
		// latest mutation function (TanStack swaps internals on each render
		// while keeping behavior consistent), and `fundingInFlightRef`
		// guards against the duplicate-run case the deps would have caught.
		// biome-ignore lint/correctness/useExhaustiveDependencies: see comment above
	}, [balance.data, onHyperliquidPage, agentApproved, t])
}
